import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { KIND_A, kindLabel } from '../lib/driverKind';
import { api, type ReqAccountKind } from '../lib/api';
import {
  ColumnBar,
  DataTable,
  ErrorBox,
  ResizeHandle,
  dash,
  money,
  moneyInput,
  commaInput,
  uncomma,
  useList,
  wallClock,
  type Column,
  type Row,
  MoneyInput,
} from '../components/DataTable';
import { colWidthStyle, useTablePrefs } from '../lib/tableCols';
import { sortRows, useSort } from '../lib/sort';
import { AccountField, ReqForm, reqKeyOf, reqTargetOf } from './ResultReq';
import Modal from '../components/Modal';
import { TopScrollbar } from '../components/TopScrollbar';
import { canScreen, useMe } from '../lib/me';
import { CommuteEditor } from './SettleCommute';
import { isVatIncl, VatPreview, VatToggle } from '../components/Vat';

/**
 * 노선 청구액의 부가세 포함 금액(10-14 #240 배시현 — 「포함·별도 칸을 따로」).
 * 포함가로 청구하는 노선(#87)은 그 포함가(단가 비율대로) · 그 밖은 공급가 + 부가세(10% 절사 · 발행 요청과 같은 규칙).
 */
function inclOf(rt: RouteRow): number {
  // 🔴 저장된 포함가가 있으면 그것(#247) — 공급가 2,454,545.4545 를 × 1.1 내림하면 2,699,999 로 1원이 빠져 보인다.
  if (rt.cost_cust_incl != null && rt.cost_cust_incl !== '') return Math.round(Number(rt.cost_cust_incl));
  const supply = Number(rt.cost_cust ?? 0);
  const unit = Number(rt.cost_cust_unit ?? 0);
  if (isVatIncl(rt.cost_cust_unit, rt.cost_cust_add_tax_unit) && unit > 0) {
    return Math.round((supply / unit) * Number(rt.cost_cust_add_tax_unit));
  }
  return supply + Math.floor(supply / 10);
}

/** 청구액 칸 옆 「포함」 표시 — 포함가로 청구하는 노선(#87). */
function VatBadge({ rt }: { rt: RouteRow }) {
  if (!isVatIncl(rt.cost_cust_unit, rt.cost_cust_add_tax_unit)) return null;
  return (
    <span
      className="ml-1 rounded bg-sky-50 px-1 py-0.5 text-[10px] font-medium text-sky-700"
      title={`부가세포함 청구 ${money(rt.cost_cust_add_tax_unit)}원 — 청구액은 공급가입니다.`}
    >
      포함
    </span>
  );
}

/**
 * 거래처 청구 상세 — 그 접수·정산월의 청구액이 **어떤 노선·차량**으로 이루어졌는지.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 왜 노선이 주 행인가 (2026-07-31 재설계)
 * ─────────────────────────────────────────────────────────────────────────
 * **청구는 노선 단위, 운행료는 배정(차량) 단위**다. 예전에는 배정 행만 나열하고
 * "그 노선의 첫 줄에만" 노선명·청구액을 찍었는데, 그 '첫 줄'을 뷰가 고르는 순서와
 * 화면이 정렬하는 순서가 달라 **노선명·운행일시가 전부 빈칸으로 보였다**
 * (통근 스냅샷 행은 `dispt_car_sch_id` 가 NULL 이라 화면 정렬의 tiebreak 이 무너진다).
 *
 * 그래서 렌더 순서에 기대는 구조를 버렸다 — 서버가 **노선 단위로 집계해서** 주고,
 * 화면은 노선을 굵은 줄로 세우고 그 밑에 배정 차량을 들여쓴다. **노선명은 늘 보인다.**
 *
 * 모수(2026-06 실측)가 이 모양을 지지한다: 노선 1,529건 중 **1,296건이 1대짜리**,
 * 접수당 상세는 **중앙값 2행**이다. 대부분은 노선 줄 하나로 끝나고, 통근 대형 접수
 * (50행 초과 10건 · 최대 177행·63노선)에서만 차량이 여러 줄 붙는다.
 * 그 10건이 정산 담당자가 실제로 시간을 쓰는 자리라 **차량은 접은 것이 기본**이다.
 */

/**
 * 이 표는 **한 칸이 두 종류의 행**을 그린다 — 노선 줄과 그 밑 차량 줄.
 * 그래서 칼럼마다 둘을 각각 어떻게 그릴지 같이 적어 둔다.
 *
 * 왜 `colSpan` 을 안 쓰는가: 칼럼 숨기기가 붙으면 `colSpan` 은 **숨긴 개수만큼 어긋난다**
 * (칸이 밀려 표가 깨진다). 칼럼마다 자기 칸을 그리게 두면 숨김·폭이 저절로 따라온다.
 */
interface BillColumn extends Column {
  /**
   * `onBulk` 가 있으면 그 노선만 대상으로 일괄 등록을 연다(쓰기 권한이 있을 때만 넘어온다).
   * `onEditCost` 가 있으면 청구액·운행료를 그 줄에서 고칠 수 있다(정산 또는 배차 권한).
   * `onEditCommute` 는 통근(스냅샷) 노선용 — 금액이 그 달 정산 줄에 있어 통근 편집기로 연다(정산 권한, #70).
   */
  route: (
    rt: RouteRow,
    open: boolean,
    onBulk?: (rt: RouteRow) => void,
    onEditCost?: (rt: RouteRow) => void,
    onEditCommute?: (rt: RouteRow, part: 'cust' | 'driver' | 'incl') => void,
  ) => ReactNode;
  /** `onAddReq` 가 있으면 그 줄에서 영업정산을 등록할 수 있다(쓰기 권한이 있을 때만 넘어온다). */
  car: (c: Row, onAddReq?: (c: Row) => void) => ReactNode;
  /** 머리글 정렬 값(#83). 없으면 같은 이름의 노선 칸 값 — 화면에서 계산하는 칸만 준다. */
  sortValue?: (rt: RouteRow) => unknown;
  /** 합계 줄에 그릴 것. 없으면 빈 칸. */
  foot?: (t: Record<string, unknown>, routes: RouteRow[]) => ReactNode;
}

/** 일괄 등록의 대상 범위. 보고 있던 줄에서 그 대상만 걸러 여는 것이 원래 동선이다. */
type BulkScope =
  | { kind: 'all'; label?: undefined; id?: undefined }
  | { kind: 'route'; id: number; label: string }
  | { kind: 'driver'; id: number; label: string };

interface RouteRow {
  dispt_id: number;
  dispt_name: string | null;
  dispt_start_time: string | null;
  start_loc: string | null;
  end_loc: string | null;
  trip_type: string | null;
  car_kind: string | null;
  car_cnt: number | null;
  from_snapshot: boolean | null;
  /** 화면 전용 — 스냅샷 노선이 **일부만**일 때 그 줄에 배지를 단다(전부면 머리에 한 번). 서버가 주지 않는다. */
  show_snap?: boolean;
  /** 화면 전용(#476) — 이 노선 배정 기사 이름·기사구분(관리기사A/B). 같은 응답의 배정 행에서 모은다. */
  drivers?: { name: string; kind: string }[];
  assign_cnt: number;
  cost_cust: string | null;
  cost_driver_total: string | null;
  /**
   * 편집용 **원값**(2026-09-17). 화면의 운행료는 "단가 x 대수" 라 총액에서 단가를 되짚으면
   * 나눠떨어지지 않는 날 금액이 어긋난다 — dispt 에서 그대로 받아 온다.
   * 과거 통근 스냅샷 행은 노선이 없을 수 있어 null 이고, 그때는 수정 버튼을 내지 않는다.
   */
  cost_cust_unit: string | null;
  cost_driver_unit: string | null;
  /** 노선의 부가세포함가 원값(#87). 공급가 × 1.1 과 다르면 「포함」 노선이다(`isVatIncl`). */
  cost_cust_add_tax_unit?: string | null;
  /** 이 달 청구 포함가 합(#247 · 계산서가 쓰는 값 — 통근 = 그 달 정산 줄). 비면 화면이 공급가 + 10% 절사. */
  cost_cust_incl?: string | null;
  /** 화면 전용 — 수정 창을 「부가세 포함」으로 연다(청구액(포함) 칸에서 눌렀을 때). */
  edit_incl?: boolean;
  /**
   * 운행료 = 단가 × 운행수(게시판 #74). 통근은 운행 일수 합, 그 밖은 배정 대수(1대 = 1).
   * 합이 운행료와 안 맞으면 서버가 비워 보낸다. 단가가 차마다 다르면 `drv_unit` 만 비고 최저·최고가 온다.
   */
  drv_unit?: string | null;
  drv_unit_min?: string;
  drv_unit_max?: string;
  drv_cnt?: number;
  /** 영업정산(수당·공제) — 서버가 이 노선의 배정들을 합쳐 준다. */
  req_cnt: number;
  driver_plus: string | null;
  driver_minus: string | null;
  cust_plus: string | null;
  cust_minus: string | null;
  /** 회사부담 비용(DYOPS 대중교통비 회사부담) — 기사 수령액이 아니라 **차익에서만** 뺀다. */
  company_cost?: string | null;
}

/**
 * 운행료에 기사 가감을 얹은 값.
 *
 * **거래처에서 보면 실지출**이고 **기사에서 보면 수령액**이다 — 같은 숫자를 양쪽에서 부르는 이름이
 * 다를 뿐이라 식도 하나만 둔다. 이게 갈리면 "거래처 차익 + 기사 수령액"이 청구액과 안 맞는다.
 */
const realCost = (r: { cost_driver_total?: unknown; driver_plus?: unknown; driver_minus?: unknown }) =>
  Number(r.cost_driver_total ?? 0) + Number(r.driver_plus ?? 0) - Number(r.driver_minus ?? 0);

/** 거래처 차익 = 청구 − 실지출 − 회사부담 비용(급여로 안 가는 비용 · _ops_transit.sql). 목록 `profit` 과 같은 식. */
const profitOf = (r: { cost_cust?: unknown; cost_driver_total?: unknown; driver_plus?: unknown; driver_minus?: unknown; company_cost?: unknown }) =>
  Number(r.cost_cust ?? 0) - realCost(r) - Number(r.company_cost ?? 0);

/**
 * 통근 달 줄의 단가·일수를 **청구 상세에서 바로** 고친다(2026-09-30 way — 「통근 운행일수를 따로 두지 말고
 * 거래처별 정산에서 바로바로, 마감 전까지」 · 버스인 기사운행료등록처럼 치고 Enter).
 * 정산 입력 권한이 있을 때만 `LineEditCtx` 가 채워진다. 마감은 서버(saveCommuteLines)가 막는다.
 */
const LineEditCtx = createContext<{ save: (c: Row, patch: { cost?: string; cnt?: string }) => Promise<void> } | null>(null);
function LineInput({ c, field, children }: { c: Row; field: 'cost' | 'cnt'; children: ReactNode }) {
  const ctx = useContext(LineEditCtx);
  const orig = field === 'cost'
    ? moneyInput(c.drv_raw_unit as string | null | undefined)
    : c.drv_raw_cnt == null ? '' : String(c.drv_raw_cnt);
  const [v, setV] = useState(orig);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(orig); }, [orig]);
  if (!ctx || c.drv_line_id == null) return <>{children}</>;
  const commit = async () => {
    if (v === orig || busy) return;
    setBusy(true);
    try {
      await ctx.save(c, field === 'cost' ? { cost: v } : { cnt: v });
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
      setV(orig);
    } finally {
      setBusy(false);
    }
  };
  const common = {
    disabled: busy,
    title: '고치고 Enter(또는 칸 밖을 누르면) 저장 · Esc 되돌리기 — 마감 전까지',
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onBlur: () => void commit(),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
      if (e.key === 'Escape') { e.preventDefault(); setV(orig); }
    },
  };
  if (field === 'cost') {
    return <MoneyInput {...common} className={`w-28 py-0.5 text-sm ${v !== orig ? 'border-brand-500' : ''}`} value={v} onChange={setV} />;
  }
  return (
    <input
      className={`field num py-0.5 text-sm w-16 ${v === '' ? 'border-red-300 bg-red-50' : ''} ${v !== orig ? 'border-brand-500' : ''}`}
      inputMode="numeric"
      value={v}
      disabled={busy}
      placeholder="일수"
      title="고치고 Enter(또는 칸 밖을 누르면) 저장 · Esc 되돌리기 — 마감 전까지"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setV(e.target.value.replace(/[^0-9]/g, ''))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
        if (e.key === 'Escape') { e.preventDefault(); setV(orig); }
      }}
      onBlur={() => void commit()}
    />
  );
}

/** 노선 줄 기사 칸(#476) — 배정 기사 이름 + 관리기사A/B 뱃지. 여러 명이면 이어 붙이고 넘치면 툴팁. */
function RouteDrivers({ list }: { list?: { name: string; kind: string }[] }) {
  if (!list || list.length === 0) return <span className="text-ink-faint">—</span>;
  const title = list.map((d) => `${d.name}${d.kind ? ` · ${kindLabel(d.kind)}` : ''}`).join('\n');
  return (
    <span className="whitespace-nowrap" title={title}>
      {list.map((d, i) => (
        <span key={i} className={i > 0 ? 'ml-2' : ''}>
          {d.name}
          {d.kind && (
            <span
              className={`ml-1 rounded px-1 py-px text-[11px] ${
                d.kind === KIND_A ? 'bg-amber-50 text-amber-800' : 'bg-canvas text-ink-soft'
              }`}
            >
              {kindLabel(d.kind)}
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

/** 운행료 단가 칸 — 한 값이면 금액, 차마다 다르면 「여러 단가」, 나눌 수 없으면 '—'. */
function DrvUnit({ unit, lo, hi }: { unit?: string | null; lo?: string; hi?: string }) {
  if (unit != null) return <>{money(unit)}</>;
  if (lo != null && hi != null) {
    return <span className="text-xs text-ink-faint" title={`${money(lo)} ~ ${money(hi)}`}>여러 단가</span>;
  }
  return <span className="text-ink-faint">—</span>;
}

/**
 * 통근 스냅샷 노선은 금액을 여기서 못 고친다 — 값이 레거시에서 매일 동기화되는 `archive` 에 있고
 * 운행 일수(`cost_driver_cnt`)도 그쪽 입력이다(#23, 2026-09-28 way 「운행일수는 레거시에 그대로」).
 * `setDisptCost` 는 라이브 `dispt` 만 고치므로 버튼을 두면 저장이 성공해도 화면 값이 안 바뀐다.
 */
const SNAP_COST_HINT = '통근 운행일수·금액은 위 「운행일수 입력」에서 수정합니다.';

const BILL_COLUMNS: BillColumn[] = [
  {
    // 노선 NO = 레거시 DisptId 와 같은 번호(배차 화면 노선 NO 와 같은 값). 차량 줄은 노선 줄에 매여 있어 비운다.
    // 과거 통근 스냅샷 노선은 번호가 없을 수 있다 → '—'.
    key: 'dispt_id',
    label: '노선 NO',
    align: 'right',
    route: (rt) => <span className="text-xs text-ink-soft">{dash(rt.dispt_id)}</span>,
    car: () => null,
  },
  {
    key: 'dispt_start_time',
    label: '운행일시',
    // 한 줄로 — 날짜와 시각이 두 줄로 갈리면 노선 줄 높이가 두 배가 된다.
    route: (rt) => <span className="whitespace-nowrap tabular-nums">{wallClock(rt.dispt_start_time)}</span>,
    car: () => null,
  },
  {
    key: 'dispt_name',
    label: '노선',
    // 배정 차량 정보가 이 칸에 실린다 — 숨기면 차량 줄이 통째로 비어 뜻을 잃는다.
    required: true,
    /**
     * **노선 줄은 한 줄로 읽힌다**(2026-09-17 사용자 요청 — "정산 중심이라 주소는 목록에 꼭 안 보여도 된다").
     * 예전엔 이름 옆에 출발·도착 **주소 전문** + 스냅샷 배지 + `+ 수당·공제` 버튼이 붙어 한 줄이
     * 세 줄로 접혔고, 금액을 세로로 훑을 수가 없었다.
     * - 주소는 **지우지 않고 툴팁으로** 옮겼다(이름에 올리면 뜬다) — 가끔 "어느 노선이더라"를 가를 때 쓴다.
     * - 스냅샷 배지는 **섞여 있을 때만** 줄마다 단다(`show_snap`). 과거 통근 접수는 전 노선이 스냅샷이라
     *   같은 배지 50개가 아무것도 가르지 못한다 — 그때는 머리에 한 번만 적는다.
     * - 노선별 `+ 수당·공제` 는 **수당·공제 칸**으로 옮겼다(그 칸의 `+`). 이름 옆에 두면 폭을 좁힌 칼럼에서
     *   버튼이 다음 줄로 떨어져 줄 높이가 다시 두 배가 된다.
     */
    route: (rt) => (
      <span
        className="font-semibold"
        title={rt.start_loc || rt.end_loc ? `${dash(rt.start_loc)} → ${dash(rt.end_loc)}` : undefined}
      >
        {dash(rt.dispt_name)}
        {rt.show_snap && (
          <span
            className="ml-2 rounded bg-violet-50 px-1 py-px text-[11px] font-medium text-violet-700"
            title="당시 확정된 정산 스냅샷에서 읽은 값입니다(과거 통근)."
          >
            스냅샷
          </span>
        )}
      </span>
    ),
    // 차량 정보는 노선 칸 아래에 들여쓴다 — 노선에 속한다는 걸 모양으로 보여준다.
    car: (c, onAddReq) => (
      <span className="pl-6 text-sm text-ink-soft">
        <span className="mr-2 text-ink-faint">└</span>
        {dash(c.car_no)}
        <span className="ml-2">{dash(c.driver_name)}</span>
        <span className="ml-2 text-xs text-ink-faint">
          {dash(c.owner_name)} · {dash(c.grp_name)}
        </span>
        {/* 수당·공제를 **이 줄에서 바로** 넣는다. 배정 스냅샷이 없는 과거 통근 행은
            붙일 대상이 없어 버튼이 안 뜬다. 통근 달 줄은 배정 번호 대신 노선·기사·차량으로 붙인다(#77). */}
        {onAddReq && reqTargetOf(c) ? (
          <button
            className="btn-ghost ml-2 px-1.5 py-0 text-xs"
            onClick={(e) => { e.stopPropagation(); onAddReq(c); }}
          >
            + 수당·공제
          </button>
        ) : null}
      </span>
    ),
    foot: (_t, routes) => (
      <>
        합계<span className="ml-1.5 font-normal text-ink-faint">노선 {routes.length}건</span>
      </>
    ),
  },
  {
    // #476 윤일향 — 「건별로 펼쳐야 기사명이 보인다」. 노선 줄에서 바로 기사·관리기사A/B 를 본다(배정 행에서 모음).
    key: 'route_drivers',
    label: '기사(관리A/B)',
    noSort: true,
    route: (rt) => <RouteDrivers list={rt.drivers} />,
    car: (c) => (
      <span className="text-xs text-ink-soft">{kindLabel(c.driver_kind) || '—'}</span>
    ),
  },
  { key: 'trip_type', label: '편도/왕복', route: (rt) => dash(rt.trip_type), car: () => null },
  { key: 'car_kind', label: '차종', route: (rt) => dash(rt.car_kind), car: () => null },
  {
    key: 'assign_cnt',
    label: '대수',
    align: 'right',
    // 대수 칸이 곧 펼침 표시다 — 몇 대가 숨어 있는지와 열렸는지를 한 자리에서 읽는다.
    route: (rt, open) => (
      <>
        <span className="mr-1 text-ink-faint">{open ? '▾' : '▸'}</span>
        {rt.assign_cnt}
      </>
    ),
    car: () => null,
    foot: (t) => money(t.car_cnt),
  },
  {
    key: 'cost_cust',
    label: '청구액(별도)',
    align: 'right',
    route: (rt, _open, _onBulk, onEditCost, onEditCommute) =>
      rt.from_snapshot === true && onEditCommute ? (
        <button
          type="button"
          className="font-semibold underline decoration-ink-faint/50 decoration-dotted underline-offset-4 hover:text-brand-700 hover:decoration-brand-500"
          title="눌러서 이 달 청구 단가·횟수 수정 (마감 전까지)"
          onClick={(e) => { e.stopPropagation(); onEditCommute(rt, 'cust'); }}
        >
          {money(rt.cost_cust)}
        </button>
      ) : onEditCost && rt.dispt_id != null && rt.from_snapshot !== true ? (
        <button
          type="button"
          // 밑줄은 **옅게** 남긴다 — 누르면 고쳐진다는 표시는 필요하지만 모든 금액이 진하게 그어지면 표가 시끄럽다.
          className="font-semibold underline decoration-ink-faint/50 decoration-dotted underline-offset-4 hover:text-brand-700 hover:decoration-brand-500"
          title="눌러서 청구액·운행료 수정 (마감 전까지)"
          onClick={(e) => { e.stopPropagation(); onEditCost(rt); }}
        >
          {money(rt.cost_cust)}
        </button>
      ) : (
        <span className="font-semibold" title={rt.from_snapshot ? SNAP_COST_HINT : undefined}>{money(rt.cost_cust)}</span>
      ),
    car: () => null,
    foot: (t) => money(t.cost_cust),
  },
  {
    // 부가세 포함(#240) — 별도 칸(공급가)과 나란히. 「포함」 표시 = 포함가로 청구하는 노선.
    key: 'cost_cust_incl',
    label: '청구액(포함)',
    align: 'right',
    // 누르면 포함가로 바로 고친다(#247 — 「1원씩 비는 것을 이 화면에서 바로」). 별도 칸과 같은 권한·조건.
    route: (rt, _open, _onBulk, onEditCost, onEditCommute) => {
      const v = <>{money(inclOf(rt))}<VatBadge rt={rt} /></>;
      const edit = rt.from_snapshot === true
        ? onEditCommute && (() => onEditCommute(rt, 'incl'))
        : onEditCost && rt.dispt_id != null && (() => onEditCost({ ...rt, edit_incl: true }));
      return edit ? (
        <button
          type="button"
          className="underline decoration-ink-faint/50 decoration-dotted underline-offset-4 hover:text-brand-700 hover:decoration-brand-500"
          title="눌러서 부가세 포함 청구액 수정 (마감 전까지)"
          onClick={(e) => { e.stopPropagation(); edit(); }}
        >
          {v}
        </button>
      ) : <span>{v}</span>;
    },
    car: () => null,
    foot: (_t, routes) => money(routes.reduce((a, rt) => a + inclOf(rt), 0)),
  },
  /**
   * 단가 · 운행수 — 운행료를 나눠 보여 준다(2026-09-30 게시판 #74 — 「버스인처럼 청구액 / 단가 / 운행수 / 운행료」,
   * 정산 뒤 더블체크용). 통근 달 줄 = 기사별 단가 × 운행 일수, 일반 = 1대당 단가 × 대수.
   * 차마다 단가가 다르면 노선 줄은 「여러 단가」(툴팁에 최저~최고)이고 차량 줄을 펴면 각자 보인다.
   */
  {
    key: 'drv_unit',
    label: '단가',
    align: 'right',
    route: (rt) => <DrvUnit unit={rt.drv_unit} lo={rt.drv_unit_min} hi={rt.drv_unit_max} />,
    car: (c) => (
      <LineInput c={c} field="cost">
        <span className="text-sm text-ink-soft">
          <DrvUnit unit={c.drv_unit as string | null | undefined} lo={c.drv_unit_min as string | undefined} hi={c.drv_unit_max as string | undefined} />
        </span>
      </LineInput>
    ),
  },
  {
    key: 'drv_cnt',
    label: '운행수',
    align: 'right',
    route: (rt) => <span title={rt.from_snapshot ? '운행 일수 합(차량별 일수를 더한 값)' : '배정 대수(1대 1회)'}>{dash(rt.drv_cnt)}</span>,
    car: (c) => <LineInput c={c} field="cnt"><span className="text-sm text-ink-soft">{dash(c.drv_cnt)}</span></LineInput>,
  },
  {
    key: 'cost_driver_total',
    label: '운행료 합계',
    align: 'right',
    route: (rt, _open, _onBulk, onEditCost, onEditCommute) =>
      rt.from_snapshot === true && onEditCommute ? (
        <button
          type="button"
          className="underline decoration-ink-faint/50 decoration-dotted underline-offset-4 hover:text-brand-700 hover:decoration-brand-500"
          title="눌러서 기사별 운행일수·단가 수정 (마감 전까지)"
          onClick={(e) => { e.stopPropagation(); onEditCommute(rt, 'driver'); }}
        >
          {money(rt.cost_driver_total)}
        </button>
      ) : onEditCost && rt.dispt_id != null && rt.from_snapshot !== true ? (
        <button
          type="button"
          className="underline decoration-ink-faint/50 decoration-dotted underline-offset-4 hover:text-brand-700 hover:decoration-brand-500"
          title="눌러서 청구액·운행료 수정 (마감 전까지)"
          onClick={(e) => { e.stopPropagation(); onEditCost(rt); }}
        >
          {money(rt.cost_driver_total)}
        </button>
      ) : (
        <span title={rt.from_snapshot ? SNAP_COST_HINT : undefined}>{money(rt.cost_driver_total)}</span>
      ),
    // 운행료만 차량 줄에 실린다 — 배정 1대분이라 노선 줄의 합과 세로로 맞아떨어진다.
    // 직영기사 배정은 운행료를 지출계에 넣지 않는다(버스인과 같은 기준 · #82) — 0 대신 이유를 보인다.
    car: (c) => c.driver_kind === '직영기사'
      ? <span className="text-xs text-ink-faint" title="관리기사A 는 급여로 따로 받아 거래처 지출계에서 운행료를 뺍니다(버스인과 같음)">직영 제외</span>
      : <span className="text-sm text-ink-soft">{money(c.cost_driver_total)}</span>,
    foot: (t) => money(t.cost_driver_total),
  },
  {
    key: 'req',
    label: '수당·공제',
    align: 'right',
    // 영업정산이 곧 지출계를 움직인다 — 여기서 바로 넣을 수 있게 차량 줄에 버튼을 둔다.
    // 노선 단위 일괄 등록 `+` 도 이 칸에 둔다 — 줄에 올리거나 펼쳤을 때만 보이고, 자리는 늘 남겨
    // 올릴 때 금액이 옆으로 밀리지 않는다. 줄 자체는 펼침 스위치라 클릭 전파를 끊는다.
    route: (rt, open, onBulk) => (
      <>
        {onBulk && (
          <button
            className={`mr-1.5 rounded border border-line px-1 text-xs leading-4 text-brand-700 hover:bg-brand-50 focus-visible:visible ${
              open ? '' : 'invisible group-hover:visible'
            }`}
            onClick={(e) => { e.stopPropagation(); onBulk(rt); }}
            title="이 노선의 배정 차량에만 수당·공제를 등록합니다"
          >
            +
          </button>
        )}
        {rt.req_cnt > 0 ? (
          <ReqAmounts plus={rt.driver_plus} minus={rt.driver_minus} count={rt.req_cnt} pick={`r${rt.dispt_id}`} />
        ) : (
          <span className="text-ink-faint">—</span>
        )}
      </>
    ),
    car: (c) => <ReqAmounts plus={c.driver_plus} minus={c.driver_minus} count={Number(c.req_cnt ?? 0)} pick={reqLineKey(c)} />,
    foot: (t) => <ReqAmounts plus={t.driver_plus} minus={t.driver_minus} count={Number(t.req_cnt ?? 0)} />,
    sortValue: (rt) => (rt.req_cnt > 0 ? Number(rt.driver_plus ?? 0) - Number(rt.driver_minus ?? 0) : null),
  },
  {
    key: 'real_cost',
    label: '실지출',
    align: 'right',
    // 운행료 + 기사지급 − 기사공제. 목록 화면의 '지출계'와 같은 정의다.
    route: (rt) => money(realCost(rt)),
    car: () => null,
    foot: (t) => money(realCost(t)),
    sortValue: (rt) => realCost(rt),
  },
  {
    key: 'profit',
    label: '차익',
    align: 'right',
    sortValue: (rt) => profitOf(rt),
    // ⚠️ 운행료가 아니라 **실지출**을 뺀다 — 수당을 넣는 순간 차익이 움직여야
    //    "수익을 보면서 수당을 넣는다"가 성립한다. 회사부담 비용(대중교통비)도 여기서 뺀다.
    route: (rt) => {
      const v = profitOf(rt);
      const cc = Number(rt.company_cost ?? 0);
      return <span className={v < 0 ? 'text-red-700' : ''} title={cc ? `회사부담 비용 ${money(cc)} 포함 차감` : undefined}>{money(v)}</span>;
    },
    car: () => null,
    foot: (t) => {
      const v = profitOf(t);
      return <span className={v < 0 ? 'text-red-700' : ''}>{money(v)}</span>;
    },
  },
];

/**
 * **기사별** 보기 — 같은 접수를 기사 축으로 다시 센 것.
 *
 * 왜 같은 화면에 두는가: 거래처 차익은 **기사에게 나가는 돈의 합**으로 정해진다.
 * 수당을 넣으면서 보는 것이 거래처 차익 하나뿐이면 "이 기사에게 얼마 갔나"는
 * 급여명세 화면으로 옮겨가 정산월 전체에서 다시 찾아야 한다 — 그건 같은 작업의 반쪽이다.
 *
 * ⚠️ 운행료 합은 노선별 보기의 합계와 **같아야 한다**(같은 모수를 축만 바꿔 센 것).
 *    다르면 어느 한쪽이 행을 잃은 것이다.
 */
const driverColumns = (onBulk?: (d: Row) => void): Column[] => [
  {
    key: 'driver_name',
    label: '기사',
    render: (r) => (
      <>
        {/* 이 기사의 배정만 대상으로 일괄 등록. 기사별 화면에서 바로 수당을 넣는 경로다. */}
        {onBulk && r.driver_id ? (
          <button
            className="btn-ghost mr-2 px-1.5 py-0 text-xs"
            onClick={(e) => { e.stopPropagation(); onBulk(r); }}
            title="이 기사의 배정에만 수당·공제를 등록합니다"
          >
            +
          </button>
        ) : null}
        <span className="font-semibold">{dash(r.driver_name)}</span>
        {r.driver_kind ? (
          <span className="ml-2 rounded bg-canvas px-1.5 py-0.5 text-xs text-ink-soft">{kindLabel(r.driver_kind)}</span>
        ) : null}
        {r.car_no ? <span className="ml-2 text-xs text-ink-faint">{String(r.car_no)}</span> : null}
      </>
    ),
  },
  { key: 'owner_name', label: '차주', render: (r) => dash(r.owner_name) },
  { key: 'grp_name', label: '소속', render: (r) => dash(r.grp_name) },
  { key: 'dispt_cnt', label: '노선', align: 'right', render: (r) => dash(r.dispt_cnt) },
  { key: 'assign_cnt', label: '대수', align: 'right', render: (r) => dash(r.assign_cnt) },
  {
    key: 'cost_driver_total',
    label: '운행료',
    align: 'right',
    render: (r) => r.driver_kind === '직영기사'
      ? <span className="text-xs text-ink-faint" title="관리기사A 는 거래처 지출계에서 운행료를 뺍니다(버스인과 같음)">직영 제외</span>
      : money(r.cost_driver_total),
  },
  {
    key: 'req',
    label: '수당·공제',
    align: 'right',
    render: (r) => <ReqAmounts plus={r.driver_plus} minus={r.driver_minus} count={Number(r.req_cnt ?? 0)} />,
    totalRender: (t) => <ReqAmounts plus={t.driver_plus} minus={t.driver_minus} count={Number(t.req_cnt ?? 0)} />,
  },
  {
    key: 'take',
    label: '기사 수령액',
    align: 'right',
    // 거래처 쪽 '실지출'과 **같은 식**이다(`realCost`) — 부르는 이름만 다르다.
    render: (r) => <b>{money(realCost(r))}</b>,
    totalRender: (t) => <b>{money(realCost(t))}</b>,
  },
];

/** 지급/공제를 한 칸에. 0 이면 그 쪽은 안 그린다 — 대부분 한쪽만 있다. */
function ReqAmounts({ plus, minus, count, pick }: { plus: unknown; minus: unknown; count: number; pick?: string }) {
  const onPick = useContext(ReqPickCtx);
  const p = Number(plus ?? 0);
  const m = Number(minus ?? 0);
  if (!p && !m) return <span className="text-ink-faint">—</span>;
  const body = (
    <>
      {p > 0 && <span className="text-emerald-700">+{money(p)}</span>}
      {p > 0 && m > 0 && <span className="mx-1 text-ink-faint">/</span>}
      {m > 0 && <span className="text-red-700">−{money(m)}</span>}
    </>
  );
  if (!onPick || !pick || count === 0) return <span title={`영업정산 ${count}건`}>{body}</span>;
  return (
    <button
      type="button"
      className="rounded underline decoration-ink-faint/50 decoration-dotted underline-offset-4 hover:bg-brand-50"
      title={`영업정산 ${count}건 — 눌러서 아래 목록에서 고치기·지우기`}
      onClick={(e) => { e.stopPropagation(); onPick(pick); }}
    >
      {body}
    </button>
  );
}

/**
 * 영업정산 금액 칸을 누르면 아래 「이 접수 영업정산」 목록이 그 줄로 좁혀진다(2026-09-30 way — 상세 안에서 고치기·지우기).
 * 키: 노선 = `r{dispt_id}` · 차량 줄 = `{dispt_id}|{driver_id}|{car_id}`(서버가 영업정산을 붙이는 키와 같다).
 */
const ReqPickCtx = createContext<((key: string) => void) | null>(null);
const reqLineKey = (r: { dispt_id?: unknown; driver_id?: unknown; car_id?: unknown }) =>
  `${r.dispt_id}|${r.driver_id ?? ''}|${r.car_id ?? ''}`;

/** 이 접수·이 달 영업정산 항목 — 줄마다 수정·삭제. 권한·마감은 서버가 판정한다(막히면 그 문구). */
function ReqList({ recvId, driveMon, nonce, focus, onFocus, canEdit, isAdmin, onChanged }: {
  recvId: number; driveMon: string; nonce: number; focus: string | null; onFocus: (k: string | null) => void;
  canEdit: boolean; isAdmin: boolean; onChanged: () => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<Row | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api.listResultReq({ recvId, driveMon, limit: 200 })
      .then((r) => { if (alive) setRows(r.rows as Row[]); })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : '영업정산을 불러오지 못했습니다.'); });
    return () => { alive = false; };
  }, [recvId, driveMon, nonce]);

  useEffect(() => {
    if (focus) boxRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focus]);

  const shown = (rows ?? []).filter((r) => {
    if (!focus) return true;
    if (focus.startsWith('r')) return String(r.dispt_id) === focus.slice(1);
    return reqLineKey(r) === focus;
  });

  async function remove(r: Row) {
    const who = `${dash(r.dispt_name)} · ${dash(r.car_no)} ${dash(r.driver_name)}`;
    if (!window.confirm(`${who} 의 영업정산을 삭제할까요?`)) return;
    setBusy(Number(r.result_req_id));
    setError(null);
    try {
      await api.deleteResultReq({ resultReqId: Number(r.result_req_id) });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const amt = (v: unknown, acct: unknown, tone: string, sign: string) =>
    Number(v ?? 0) ? (
      <span className={tone}>
        {sign}{money(v as string)}
        {acct ? <span className="ml-1 text-xs text-ink-faint">{String(acct)}</span> : null}
      </span>
    ) : <span className="text-ink-faint">—</span>;

  if (rows && rows.length === 0 && !error) return null;
  return (
    <div ref={boxRef} className="rounded-lg border border-line">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-canvas px-3 py-2 text-sm">
        <b>이 접수 영업정산</b>
        <span className="text-xs text-ink-soft">{rows ? `${rows.length}건` : '불러오는 중…'}</span>
        {focus && (
          <>
            <span className="rounded bg-brand-50 px-1.5 text-xs text-brand-700">고른 줄만 {shown.length}건</span>
            <button className="btn-ghost px-1.5 py-0 text-xs" onClick={() => onFocus(null)}>전체 보기</button>
          </>
        )}
        <span className="ml-auto text-xs text-ink-faint">표의 영업정산 금액을 누르면 그 줄 항목만 보입니다.</span>
      </div>
      {error && <div className="p-2"><ErrorBox message={error} /></div>}
      {shown.length > 0 && (
        <div className="max-h-80 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="th">노선</th>
                <th className="th">차량 · 기사</th>
                <th className="th text-right">거래처 청구</th>
                <th className="th text-right">거래처 공제</th>
                <th className="th text-right">기사 지급</th>
                <th className="th text-right">기사 공제</th>
                <th className="th text-right" title="회사가 부담하는 비용 — 급여에는 안 들어가고 거래처 차익에서만 빠집니다">회사부담</th>
                <th className="th">비고</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const id = Number(r.result_req_id);
                const locked = r.is_locked === true && !isAdmin;
                return (
                  <tr key={id} className="border-t border-line">
                    <td className="td">
                      {dash(r.dispt_name)}
                      <span className="ml-1 text-xs text-ink-faint">NO {dash(r.dispt_id)}</span>
                    </td>
                    <td className="td whitespace-nowrap">{dash(r.car_no)} · {dash(r.driver_name)}</td>
                    <td className="td text-right">{amt(r.cust_plus, r.cust_plus_account, 'text-emerald-700', '+')}</td>
                    <td className="td text-right">{amt(r.cust_minus, r.cust_minus_account, 'text-red-700', '−')}</td>
                    <td className="td text-right">{amt(r.driver_plus, r.driver_plus_account, 'text-emerald-700', '+')}</td>
                    <td className="td text-right">{amt(r.driver_minus, r.driver_minus_account, 'text-red-700', '−')}</td>
                    {/* 회사부담(DYOPS 대중교통비 회사부담 등) — 이 칸이 없으면 금액이 「—」 만 보여 미반영처럼 읽혔다(10-07 회계) */}
                    <td className="td text-right">{amt(r.company_cost, r.company_cost_account, 'text-amber-700', '−')}</td>
                    <td className="td max-w-[16rem] truncate text-xs text-ink-soft" title={String(r.comment ?? '')}>
                      {r.is_locked === true && <span className="mr-1" title="선지급 — 관리자만 수정">🔒</span>}
                      {dash(r.comment)}
                    </td>
                    <td className="td whitespace-nowrap text-right">
                      {canEdit && (
                        <>
                          <button className="btn-ghost px-1.5 py-0 text-xs" disabled={locked || busy === id}
                            title={locked ? '선지급 공제는 관리자만 고칩니다' : '이 항목 수정'} onClick={() => setEdit(r)}>수정</button>
                          <button className="btn-ghost px-1.5 py-0 text-xs text-red-600" disabled={locked || busy === id}
                            title={locked ? '선지급 공제는 관리자만 지웁니다' : '이 항목 삭제'} onClick={() => void remove(r)}>
                            {busy === id ? '삭제 중…' : '삭제'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <Modal>
          <ReqForm
            row={edit}
            driveMon={driveMon}
            isAdmin={isAdmin}
            onClose={() => setEdit(null)}
            onSaved={() => { setEdit(null); onChanged(); }}
          />
        </Modal>
      )}
    </div>
  );
}


export default function CustBillDetail({ row, onClose, onMisu, canMarkMisu = false, nav }: {
  row: Row; onClose: () => void; onMisu?: (r: Row) => void;
  /** 정산 입력 권한 — 청구 기록이 없을 때 「청구대상 표시」를 보인다(그 밖은 「청구 정보」 보기). */
  canMarkMisu?: boolean;
  /** 목록에서 이전·다음 접수로(지금 목록 쪽 안에서) — 목록으로 돌아가 다시 고르지 않게(10-29 way). */
  nav?: { idx: number; count: number; go: (d: -1 | 1) => void };
}) {
  const recvId = Number(row.recv_id);
  const driveMon = String(row.drive_mon ?? '');

  // 이 표도 칼럼 폭·숨김을 기억한다(`lib/tableCols.ts`). 공용 DataTable 을 안 쓰므로 직접 붙인다.
  const { prefs, isHidden, toggleHidden, setWidth, reset, customized, savedTick } = useTablePrefs('settle.custBillDetail');
  const shownCols = BILL_COLUMNS.filter((c) => !isHidden(c.key));
  const cols = shownCols.length > 0 ? shownCols : BILL_COLUMNS;
  const anyWidth = Object.keys(prefs.width).length > 0;
  // 폭을 좁혔을 때 옆 칸을 덮지 않게 — 이유는 `components/DataTable.tsx` 의 `cellClip` 주석 참고.
  const cellClip = (c: BillColumn) =>
    !anyWidth ? '' : c.align === 'right' ? 'overflow-hidden text-ellipsis' : 'overflow-hidden whitespace-normal break-keep';

  /**
   * 펼친 노선들. **기본은 전부 접힘** — 노선 줄만 남아야 한 판에 들어온다.
   *
   * 노선별로 여는 이유: 정산은 "이 노선 금액이 왜 이렇지"를 한 줄씩 따지는 일이라
   * 보통은 **한 노선만** 열면 된다. 전체를 열면 서울대(99대)처럼 다시 스크롤 지옥이 된다.
   * 전체 펴기는 남겨 둔다 — 인쇄·대조처럼 전부 훑어야 할 때가 있다.
   */
  const [open, setOpen] = useState<Set<number>>(new Set());
  /** 수당·공제를 넣을 대상 배정. 열려 있으면 그 줄로 영업정산 폼이 뜬다. */
  const [reqFor, setReqFor] = useState<Row | null>(null);
  /** 아래 「이 접수 영업정산」 목록을 좁히는 줄(표의 영업정산 금액을 눌렀을 때). */
  const [reqFocus, setReqFocus] = useState<string | null>(null);
  /** 저장하면 상세를 다시 읽어 **차익이 그 자리에서 움직이는지** 보이게 한다. */
  const [nonce, setNonce] = useState(0);
  /** 같은 모수를 **노선 축 / 기사 축**으로 본다. 축을 바꿔도 다시 불러오지 않는다(한 응답이다). */
  const [view, setView] = useState<'route' | 'driver'>('route');
  /**
   * 일괄 등록 패널 + **대상 범위**. 전체 / 이 노선만 / 이 기사만.
   *
   * 왜 범위가 필요한가 (2026-08-01 사용자 지적): 접수 하나에 배정이 수십 대면 전량 목록에서
   * 내 대상을 찾아 스크롤해야 한다. 수당은 보통 **한 노선 통째로**(그 노선 기사 전원 식대) 또는
   * **한 기사에게**(반장수당) 붙으므로, 보고 있던 줄에서 그 대상만 걸러 여는 것이 원래 동선이다.
   */
  const [bulk, setBulk] = useState<BulkScope | null>(null);
  const { me } = useMe();
  const canWriteReq = canScreen(me, ['result-req', 'settle']);
  /** 목록(listResultReq)은 영업정산 조회 권한 — 없으면 목록·금액 누르기를 안 낸다. */
  const canReadReq = canScreen(me, ['result-req'], 'read');
  /**
   * 청구액·운행료 수정(2026-09-17 사용자 요청) — **정산 또는 배차**(사용자 확정).
   * 정산 담당자가 정산 화면에서 고치는 것이 이 요청의 목적이다.
   * 🔴 **마감 여부는 화면이 판정하지 않는다** — 서버의 `assertMonthOpen` 이 정본이고,
   *    막히면 그 문구를 그대로 보여준다. 여기서 한 번 더 판정하면 두 규칙이 갈린다.
   */
  const canEditCost = canScreen(me, ['settle', 'dispatch']);
  const [costFor, setCostFor] = useState<RouteRow | null>(null);
  const [commuteOpen, setCommuteOpen] = useState(false);
  /** 통근 편집기를 열 때 바로 띄울 노선의 청구 금액 창(#70 — 청구액 칸을 눌러 열었을 때). */
  const [commuteRoute, setCommuteRoute] = useState<number | null>(null);
  const canCommute = canScreen(me, ['settle']);
  const [commuteIncl, setCommuteIncl] = useState(false);
  const editCommute = useCallback((rt: RouteRow, part: 'cust' | 'driver' | 'incl') => {
    setCommuteRoute(part === 'driver' ? null : Number(rt.dispt_id));
    setCommuteIncl(part === 'incl');
    setCommuteOpen(true);
  }, []);

  const { rows, loading, error, total, routes: rawRoutes, drivers, directCars } = useDetail(recvId, driveMon, nonce);
  /** 스냅샷 노선 수. 전부면 머리에 한 번, 일부면 그 줄에만 배지(노선 칸 주석 참고). */
  const snapCnt = rawRoutes.filter((r) => r.from_snapshot === true).length;
  const allSnap = rawRoutes.length > 0 && snapCnt === rawRoutes.length;
  /** 노선별 배정 기사(#476) — 같은 응답의 배정 행에서 이름·기사구분을 모은다(같은 기사 두 번이면 한 번). */
  const driversByRoute = useMemo(() => {
    const m = new Map<number, { name: string; kind: string }[]>();
    for (const r of rows) {
      const name = r.driver_name == null ? '' : String(r.driver_name).trim();
      if (!name) continue;
      const id = Number(r.dispt_id);
      const kind = r.driver_kind == null ? '' : String(r.driver_kind);
      const list = m.get(id) ?? [];
      if (!list.some((x) => x.name === name && x.kind === kind)) list.push({ name, kind });
      m.set(id, list);
    }
    return m;
  }, [rows]);
  const routes = useMemo(
    () => rawRoutes.map((r) => ({
      ...r,
      show_snap: r.from_snapshot === true && !allSnap,
      drivers: driversByRoute.get(Number(r.dispt_id)) ?? [],
    })),
    [rawRoutes, allSnap, driversByRoute],
  );
  /**
   * 노선 머리글 정렬(게시판 #83 — 「버스인은 정산 화면마다 노선별 오름·내림 정렬이 됐다」).
   * 서버 순서(운행일시)는 통근 노선이면 등록 시각이라 퇴근이 출근 앞에 오기도 한다. 노선은 접수 하나 분량을
   * 전부 받아 두므로 화면에서 정렬한다(issues 107 은 200줄씩 끊어 받는 목록 얘기). 고른 정렬은 기억된다.
   */
  const [routeSort, onRouteSort] = useSort('custBillRoutes');
  const sortedRoutes = useMemo(
    () => sortRows(routes, routeSort, (rt, k) => {
      const col = BILL_COLUMNS.find((c) => c.key === k);
      return col?.sortValue ? col.sortValue(rt) : (rt as unknown as Record<string, unknown>)[k];
    }),
    [routes, routeSort],
  );
  const [driverSort, onDriverSort] = useSort('custBillDrivers');
  const sortedDrivers = useMemo(
    () => sortRows(drivers as unknown as Row[], driverSort, (r, k) =>
      k === 'take' ? realCost(r)
        : k === 'req' ? (Number(r.req_cnt ?? 0) > 0 ? Number(r.driver_plus ?? 0) - Number(r.driver_minus ?? 0) : null)
          : r[k]),
    [drivers, driverSort],
  );
  const allOpen = routes.length > 0 && open.size === routes.length;

  /**
   * 노선을 누르면 **그 노선 하나만** 남기고 나머지는 숨긴다(2026-09-30 way 요청 — 「노선 선택 시 노선은 한 개만
   * 보이고 아래 세부항목이 한 번에 많이 보이게」). 같은 줄을 다시 누르거나 「전체 노선」을 누르면 목록으로 돌아간다.
   * 전체 펴기(인쇄·대조용)는 숨기지 않고 전부 편다.
   */
  const toggle = (id: number) =>
    setOpen((cur) => (cur.size === 1 && cur.has(id) ? new Set() : new Set([id])));
  const focusId = open.size === 1 && routes.length > 1 ? [...open][0] : null;
  const shownRoutes = focusId == null ? sortedRoutes : sortedRoutes.filter((r) => Number(r.dispt_id) === focusId);
  const focusIdx = focusId == null ? -1 : sortedRoutes.findIndex((r) => Number(r.dispt_id) === focusId);
  const focusStep = (d: number) => {
    const next = sortedRoutes[focusIdx + d];
    if (next) setOpen(new Set([Number(next.dispt_id)]));
  };

  /** 배정 행을 노선별로 묶어 둔다. 노선 줄 밑에서 이걸 꺼내 쓴다. */
  const carsByRoute = useMemo(() => {
    const m = new Map<number, Row[]>();
    for (const r of rows) {
      const id = Number(r.dispt_id);
      const list = m.get(id);
      if (list) list.push(r);
      else m.set(id, [r]);
    }
    return m;
  }, [rows]);

  /** 노선별 표 상자 — 위 가로 스크롤 막대(TopScrollbar)가 같이 민다. */
  const billBoxRef = useRef<HTMLDivElement>(null);
  const custTotal = Number(total?.values.cost_cust ?? 0);
  const drvTotal = Number(total?.values.cost_driver_total ?? 0);
  const lineEdit = useMemo(() => (canCommute ? {
    save: async (c: Row, patch: { cost?: string; cnt?: string }) => {
      const cnt = patch.cnt !== undefined ? patch.cnt : c.drv_raw_cnt == null ? null : String(c.drv_raw_cnt);
      const costDriver = patch.cost !== undefined ? patch.cost : String(c.drv_raw_unit ?? '');
      await api.saveCommuteLines({ items: [{ id: Number(c.drv_line_id), cnt: cnt == null || cnt === '' ? null : Number(cnt), costDriver: costDriver === '' ? null : costDriver }] });
      setNonce((n) => n + 1);
    },
  } : null), [canCommute]);

  return (
    <LineEditCtx.Provider value={lineEdit}>
    <ReqPickCtx.Provider value={canReadReq ? setReqFocus : null}>
    <div className="card space-y-2 p-3">
      {/* 머리는 **한 줄**(10-29 way 「최대한 상단은 한줄로 하단 내용 위주로」) — 넘치면 그 줄 안에서 가로로 민다(막대는 올릴 때만). */}
      <div className="scroll-quiet flex items-center gap-2 overflow-x-auto whitespace-nowrap">
        {/* 머리 띠는 목록의 고른 줄과 같은 노랑 — 「저 노란 줄 = 이 상세」가 바로 이어지게(10-29 way). */}
        {/* 노선·배정 수는 띠에서 뺐다(한 줄에 안 들어간다) — 합계 줄(머리글 밑)에 같은 값이 있고 띠 툴팁에도 남긴다. */}
        <div
          className="shrink-0 rounded-md border-l-4 border-amber-400 bg-amber-100 px-2 py-1 text-sm font-bold"
          title={loading ? undefined : `노선 ${routes.length}건 · 배정 ${total?.count ?? 0}대`}
        >
          접수 {recvId} · {dash(row.cust_name)} · {driveMon}
          {allSnap && (
            <span
              className="ml-2 rounded bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-700"
              title="이 접수의 노선은 전부 당시 확정된 정산 스냅샷에서 읽은 값입니다(과거 통근)."
            >
              스냅샷
            </span>
          )}
          {/* 발행(청구)법인 — 접수에 적힌 청구법인(#397). 계산서가 이 법인으로 나간다. */}
          {row.fin_grp_name ? (
            <span className="ml-2 font-bold" title="발행법인 — 접수에 등록된 청구법인입니다.">
              {String(row.fin_grp_name)}
            </span>
          ) : null}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2 text-sm">
          {/* 축 바꾸기 — 같은 응답을 다시 세는 것이라 서버를 또 부르지 않는다. */}
          <div className="flex gap-1">
            {(
              [
                ['route', '노선별'],
                ['driver', '기사별'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setView(id)}
                className={`rounded-lg px-2.5 py-1 text-xs font-medium ${
                  view === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* 청구 정보 — 목록 칸 버튼과 같은 창(way 10-02 「여기서 청구신청이 어디있지?」 → 오후: 여기선 청구대상만). */}
          {onMisu && (canMarkMisu || String(row.misu_req_status ?? '').trim() !== '') && (
            <button className="btn-ghost py-1 text-xs" onClick={() => onMisu(row)}
              title="청구 요청(청구대상)부터 청구 금액이 잠깁니다 — 금액 수정·청구 취소는 「청구 정보」 → 「취소·수정 승인요청」으로 회계팀 승인 · 선지급·명세 공제도 청구 요청부터">
              {String(row.misu_req_status ?? '').trim() === '청구대상' && canMarkMisu ? '청구대상 해제'
                : String(row.misu_req_status ?? '').trim() ? `청구 정보 (${String(row.misu_req_status).trim()})` : '청구대상 표시'}
            </button>
          )}
          {canWriteReq && (
            <button
              className={bulk ? 'btn-primary py-1 text-xs' : 'btn-ghost py-1 text-xs'}
              onClick={() => setBulk((b) => (b ? null : { kind: 'all' }))}
            >
              {bulk ? '일괄 등록 닫기' : '수당·공제 등록'}
            </button>
          )}
          {/* 통근(스냅샷 노선)은 달마다 정산 줄에 일수를 적는다 — 그 편집기를 여기서 바로 연다(#51). */}
          {snapCnt > 0 && (
            <button className="btn-ghost py-1 text-xs" onClick={() => setCommuteOpen(true)}
              title="노선 빼기·기사 넣기/빼기·배차에서 다시 불러오기·다른 달 그대로 가져오기">
              노선·기사 편집
            </button>
          )}
          {view === 'route' && (
            <button
              className="btn-ghost py-1 text-xs"
              disabled={routes.length === 0}
              onClick={() => setOpen(allOpen ? new Set() : new Set(routes.map((r) => Number(r.dispt_id))))}
            >
              {allOpen ? '차량 접기' : '차량 펴기'}
            </button>
          )}
          {view === 'route' && (
            <ColumnBar
              columns={BILL_COLUMNS}
              isHidden={isHidden}
              toggleHidden={toggleHidden}
              reset={reset}
              customized={customized}
              savedTick={savedTick}
              shownCount={cols.length}
            />
          )}
          {nav && nav.count > 1 && (
            <span className="flex items-center gap-1 text-xs">
              <button className="btn-ghost px-2 py-1 text-xs" disabled={nav.idx <= 0} onClick={() => nav.go(-1)} title="이전 접수(목록의 바로 위 줄)">
                ◀
              </button>
              <span className="text-ink-soft tabular-nums">접수 {nav.idx + 1}/{nav.count}</span>
              <button className="btn-ghost px-2 py-1 text-xs" disabled={nav.idx >= nav.count - 1} onClick={() => nav.go(1)} title="다음 접수(목록의 바로 아래 줄)">
                ▶
              </button>
            </span>
          )}
          {/* 닫기는 줄이 넘쳐 밀려도 오른쪽 끝에 늘 보이게. */}
          <button className="btn-ghost sticky right-0 bg-surface" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>

      {error && <ErrorBox message={error} />}

      {commuteOpen && (
        <Modal width="max-w-6xl">
          <CommuteEditor
            recvId={recvId}
            driveMon={driveMon}
            canWrite={canCommute}
            openRoute={commuteRoute}
            openRouteIncl={commuteIncl}
            onClose={() => { setCommuteOpen(false); setCommuteRoute(null); setCommuteIncl(false); setNonce((n) => n + 1); }}
            onReq={canWriteReq ? (l) => {
              setCommuteOpen(false); setCommuteRoute(null); setCommuteIncl(false); setNonce((n) => n + 1);
              setBulk({ kind: 'driver', id: Number(l.driver_id), label: `${l.driver_name ?? ''}${l.car_no ? ` · ${l.car_no}` : ''}` });
            } : undefined}
          />
        </Modal>
      )}

      {/* 금액 수정도 **팝업**이다 — 표 위에 인라인으로 열면 어느 줄을 고치는지 잃는다(issues 90). */}
      {costFor && (
        <Modal>
          <RouteCostForm
            route={costFor}
            driveMon={driveMon}
            onCancel={() => setCostFor(null)}
            onSaved={() => { setCostFor(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      {/* 금액 입력은 **팝업**으로 띄운다 — 여백·Esc 로 안 닫혀서(components/Modal.tsx) 옆을
          잘못 눌러 적던 금액이 사라지지 않고, 뒤 표의 숫자 칸과 섞여 엉뚱한 줄에 치는 일도 없다.
          닫는 길은 폼의 `취소`·`등록` 뿐이다. 노선 표를 품으므로 폭은 넓게 잡는다. */}
      {bulk && canWriteReq && (
        <Modal width="max-w-5xl">
          <BulkReqPanel
            driveMon={driveMon}
            rows={rows}
            routes={routes}
            scope={bulk}
            onScope={setBulk}
            isAdmin={me?.role === 'admin'}
            onClose={() => setBulk(null)}
            onDone={() => { setBulk(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      {view === 'driver' && (
        <>
          <DataTable
            columns={driverColumns(
              canWriteReq
                ? (d) =>
                    setBulk({
                      kind: 'driver',
                      id: Number(d.driver_id),
                      label: `${dash(d.driver_name)} (${dash(d.car_no)})`,
                    })
                : undefined,
            )}
            rows={sortedDrivers}
            loading={loading}
            sort={driverSort}
            onSort={onDriverSort}
            tableId="settle.custBillDrivers"
            totalAt="top"
            total={
              total
                ? {
                    count: drivers.length,
                    label: '총계',
                    // 합계는 **서버가 낸 값 그대로** 쓴다 — 기사 줄을 화면에서 더하지 않는다.
                    // 노선수·대수만 이름을 맞춰 옮긴다(총계는 접수 전체 기준이라 값이 같다).
                    values: { ...total.values, dispt_cnt: total.values.dispt_count, assign_cnt: total.count },
                  }
                : null
            }
          />
          <p className="break-keep text-xs text-ink-soft">
            같은 접수를 <b>기사 축</b>으로 다시 센 것입니다 — 운행료 합은 노선별 보기와 같습니다.
            <b> 기사 수령액</b> = 운행료 + 수당 − 공제이고, 거래처 쪽에서는 같은 값이 <b>실지출</b>로 잡힙니다.
            (실제 지급처는 기사구분에 따라 다릅니다 — 관리기사B 는 차주에게 나갑니다.)
          </p>
        </>
      )}

      {view === 'route' && (
      <>
      {/* 노선 하나만 볼 때만 둘째 줄(항목관리는 머리 줄로 올렸다). */}
      {focusId != null && (
      <div className="flex flex-wrap items-center gap-2">
          <div className="ml-auto flex items-center gap-1 text-xs">
            <span className="text-ink-soft">노선 {focusIdx + 1}/{sortedRoutes.length}</span>
            <button className="btn-ghost py-0.5 text-xs" disabled={focusIdx <= 0} onClick={() => focusStep(-1)}>
              ◀ 이전 노선
            </button>
            <button className="btn-ghost py-0.5 text-xs" disabled={focusIdx >= sortedRoutes.length - 1} onClick={() => focusStep(1)}>
              다음 노선 ▶
            </button>
            <button className="btn-primary py-0.5 text-xs" onClick={() => setOpen(new Set())}>
              전체 노선 {sortedRoutes.length}건 보기
            </button>
          </div>
      </div>
      )}

      {/* 표는 **화면 높이 안에서** 스크롤한다 — 머리글·합계가 붙어 있어 차량이 많아도 어느 칸인지 잃지 않는다. */}
      <TopScrollbar target={billBoxRef} />
      <div ref={billBoxRef} className="max-h-[calc(var(--app-h)-9rem)] overflow-auto rounded-lg border border-line">
        <table className={`w-full ${anyWidth ? 'table-fixed' : ''}`}>
          {anyWidth && (
            <colgroup>
              {cols.map((c) => (
                <col key={c.key} style={colWidthStyle(prefs, c.key)} />
              ))}
            </colgroup>
          )}
          {/* 머리글 + 합계를 **한 덩어리로** 위에 붙인다(10-29 way 「소계 등은 가능하면 위로」 · DataTable totalAt="top" 과 같은 방식).
              배경은 thead 가 아니라 칸마다 — sticky 상태에서 row-group 배경은 안 칠해진다. */}
          <thead className="sticky top-0 z-10">
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={`th bg-surface ${c.align === 'right' ? 'text-right' : ''}`}
                  // 좁히면 머리글도 잘린다 — 어느 칸인지 잃지 않게 전체 이름을 툴팁으로 남긴다.
                  title={typeof c.label === 'string' ? c.label : undefined}
                >
                  <button
                    type="button"
                    className={`flex w-full items-center gap-1 font-semibold hover:text-brand-700 ${c.align === 'right' ? 'flex-row-reverse text-right' : 'text-left'}`}
                    onClick={() => onRouteSort(c.key)}
                    title="눌러서 오름차순 → 내림차순 → 원래 순서"
                  >
                    <span className={anyWidth ? 'block truncate' : undefined}>{c.label}</span>
                    <span className={`inline-block w-3 shrink-0 text-center text-[10px] ${routeSort?.key === c.key ? 'text-brand-700' : 'text-ink-faint/40'}`}>
                      {routeSort?.key === c.key ? (routeSort.dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                  <ResizeHandle onResize={(px) => setWidth(c.key, px)} />
                </th>
              ))}
            </tr>
            {total && (
              <tr className="font-semibold">
                {cols.map((c) => (
                  <td key={c.key} className={`td border-b-2 border-line bg-canvas ${c.align === 'right' ? 'num' : ''} ${cellClip(c)}`}>
                    {c.foot ? c.foot(total.values, routes) : ''}
                  </td>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {shownRoutes.map((rt) => (
              <RouteGroup
                key={rt.dispt_id}
                cols={cols}
                clip={cellClip}
                onAddReq={canWriteReq ? setReqFor : undefined}
                onBulk={
                  canWriteReq
                    ? (r) => setBulk({ kind: 'route', id: Number(r.dispt_id), label: dash(r.dispt_name) })
                    : undefined
                }
                onEditCost={canEditCost ? setCostFor : undefined}
                onEditCommute={canCommute ? editCommute : undefined}
                rt={rt}
                cars={carsByRoute.get(Number(rt.dispt_id)) ?? []}
                showCars={open.has(Number(rt.dispt_id))}
                onToggle={() => toggle(Number(rt.dispt_id))}
              />
            ))}
            {!loading && routes.length === 0 && (
              <tr>
                <td className="td text-ink-faint" colSpan={cols.length}>
                  이 접수·정산월에는 배차완료된 노선이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* ⚠️ 상세 합계와 목록의 지출계는 **원래 다르다** — 상세는 배정별 운행료 원값이고
          목록은 거기에 영업정산 가감을 더한 값이다. 실측(접수 13909·2026-06):
          282,545,000 + 15,103,000 − 6,100,000 = 291,548,000.
          이 대사를 화면에 안 적으면 정산할 때 "어느 쪽이 틀렸지"로 멈춘다. */}
      {canReadReq && (
        <ReqList
          recvId={recvId}
          driveMon={driveMon}
          nonce={nonce}
          focus={reqFocus}
          onFocus={setReqFocus}
          canEdit={canScreen(me, ['result-req'])}
          isAdmin={me?.role === 'admin'}
          onChanged={() => setNonce((n) => n + 1)}
        />
      )}

      <Reconcile row={row} detailDriver={drvTotal} detailCust={custTotal} directCars={directCars} />

      {reqFor && (
        <Modal>
          <ReqForm
            row="new"
            driveMon={driveMon}
            isAdmin={me?.role === 'admin'}
            preset={{
              target: reqTargetOf(reqFor)!,
              label: `${dash(reqFor.car_no)} · ${dash(reqFor.driver_name)} · ${dash(reqFor.dispt_name)}`,
            }}
            onClose={() => setReqFor(null)}
            onSaved={() => { setReqFor(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      <p className="break-keep text-xs text-ink-soft">
        <b>청구액은 노선 단위</b>, <b>운행료는 차량 1대당</b>입니다 — 노선 줄의 운행료는 그 노선에 들어간
        차량들의 합입니다. 과거 통근은 당시 확정된 <b>정산 스냅샷</b>을 읽어 배차를 고쳐도 바뀌지 않습니다.
        {canCommute && <> 통근 노선은 줄을 펼쳐 차량별 <b>단가·운행수</b>를 바로 고칠 수 있습니다(Enter 저장 · 마감 전까지).</>}
      </p>
    </div>
    </ReqPickCtx.Provider>
    </LineEditCtx.Provider>
  );
}

/**
 * 상세 합계 → 목록의 수입계·지출계로 **대사(對査)**.
 *
 * 둘은 원래 다르다 — 상세는 배정별 원값이고 목록은 **영업정산 가감**을 얹은 값이다.
 * 그 차이를 화면에 안 적으면 정산하는 사람이 "어느 쪽이 틀렸지"에서 멈춘다.
 * 가감이 0 이면 줄이 안 뜬다(대부분의 접수가 그렇다 — 없는 설명을 늘어놓지 않는다).
 */
type DirectCar = { car_id: number; car_no: string | null; direct_cost: string };

function Reconcile({
  row,
  detailCust,
  detailDriver,
  directCars,
}: {
  row: Row;
  detailCust: number;
  detailDriver: number;
  directCars: DirectCar[];
}) {
  const custPlus = Number(row.cust_plus ?? 0);
  const custMinus = Number(row.cust_minus ?? 0);
  const drvPlus = Number(row.driver_plus ?? 0);
  const drvMinus = Number(row.driver_minus ?? 0);
  const listCust = Number(row.cost_cust_total ?? 0);
  const listDrv = Number(row.cost_driver_total ?? 0);
  const adjusted = custPlus || custMinus || drvPlus || drvMinus;

  // 가감이 없는데도 어긋나면 그건 진짜 이상이다 — 그때는 숨기지 말고 드러낸다.
  const mismatch = !adjusted && (listCust !== detailCust || listDrv !== detailDriver);
  // 실질수익(10-21 way) — 관리기사A 차량 비용을 운행료 비중으로 나눠 붙인 값. 목록 줄과 같은 숫자를 차량별로 푼다.
  // 0 이면(관리기사A 가 안 도는 접수) 안 그린다 · 그 달 명세 전(null)도 안 그린다(목록이 「—」로 이미 말한다).
  const directCost = row.direct_cost == null ? null : Number(row.direct_cost);
  const showDirect = directCost != null && directCost !== 0;
  if (!adjusted && !mismatch && !showDirect) return null;

  return (
    <div className="rounded-lg border border-line bg-canvas px-3 py-2 text-xs">
      {(adjusted || mismatch) && (
      <div className="grid grid-cols-[10rem_1fr_1fr] gap-x-3 gap-y-1 tabular-nums">
        <span className="text-ink-soft">위 노선 합계</span>
        <span className="text-right">청구 {money(detailCust)}</span>
        <span className="text-right">운행료 {money(detailDriver)}</span>

        <span className="text-ink-soft">영업정산 가감</span>
        <span className="text-right text-ink-soft">
          {custPlus || custMinus ? `+${money(custPlus)} / −${money(custMinus)}` : '—'}
        </span>
        <span className="text-right text-ink-soft">
          {drvPlus || drvMinus ? `+${money(drvPlus)} / −${money(drvMinus)}` : '—'}
        </span>

        <span className="border-t border-line pt-1 font-semibold">청구서 기준</span>
        <span className="border-t border-line pt-1 text-right font-semibold">수입계 {money(listCust)}</span>
        <span className="border-t border-line pt-1 text-right font-semibold">지출계 {money(listDrv)}</span>
      </div>
      )}
      {showDirect && (
        <div className={`grid grid-cols-[10rem_1fr] gap-x-3 gap-y-1 tabular-nums ${adjusted || mismatch ? 'mt-2 border-t border-dashed border-line pt-2' : ''}`}>
          <span className="text-ink-soft">수익</span>
          <span className="text-right">{money(row.profit)}</span>
          <span className="text-ink-soft" title="관리기사A 차량의 그 달 급여(월정기·재무추가)·할부금을 그 차량 운행료 비중으로 나눠 이 접수에 붙인 값">
            관리기사A 직영비용
          </span>
          <span className="text-right text-red-700">−{money(directCost)}</span>
          {directCars.map((c) => (
            <Fragment key={c.car_id}>
              <span className="pl-3 text-ink-faint">{dash(c.car_no)}</span>
              <span className="text-right text-ink-faint">{money(c.direct_cost)}</span>
            </Fragment>
          ))}
          <span className="border-t border-line pt-1 font-semibold">실질수익</span>
          <span className={`border-t border-line pt-1 text-right font-semibold ${Number(row.real_profit) < 0 ? 'text-red-700' : ''}`}>
            {money(row.real_profit)}
          </span>
        </div>
      )}
      {mismatch && (
        <p className="mt-1 text-amber-700">
          가감이 없는데 상세 합계와 청구서 기준이 다릅니다 — 스냅샷과 현재 배차가 어긋난 건일 수 있습니다.
        </p>
      )}
    </div>
  );
}

/**
 * 노선 한 줄 + (펴면) 그 노선에 들어간 차량들. 노선 사이는 굵은 선으로 갈린다.
 * **줄 전체가 펼침 스위치**다 — 대수 칸의 작은 삼각형만 누르게 하면 어디를 눌러야 할지 찾게 된다.
 */
function RouteGroup({
  cols,
  clip,
  onAddReq,
  onBulk,
  onEditCost,
  onEditCommute,
  rt,
  cars,
  showCars,
  onToggle,
}: {
  cols: BillColumn[];
  /** 좁힌 칼럼이 옆 칸을 덮지 않게 하는 클래스. 부모가 폭 설정을 알고 있어 넘겨받는다. */
  clip: (c: BillColumn) => string;
  /** 쓰기 권한이 있을 때만 넘어온다. 없으면 차량 줄에 등록 버튼이 안 뜬다. */
  onAddReq?: (c: Row) => void;
  /** 노선 줄의 일괄 등록 버튼. 그 노선의 배정만 대상으로 패널을 연다. */
  onBulk?: (rt: RouteRow) => void;
  /** 청구액·운행료 수정 팝업을 연다. 권한이 없거나 스냅샷 행이면 안 넘어온다. */
  onEditCost?: (rt: RouteRow) => void;
  /** 통근(스냅샷) 노선의 금액을 통근 편집기로 연다. 정산 권한이 없으면 안 넘어온다. */
  onEditCommute?: (rt: RouteRow, part: 'cust' | 'driver' | 'incl') => void;
  rt: RouteRow;
  cars: Row[];
  showCars: boolean;
  onToggle: () => void;
}) {
  // 서버가 센 대수와 실제로 받은 행 수가 다르면 목록이 상한에 걸린 것이다 — 조용히 두지 않는다.
  const truncated = showCars && cars.length > 0 && cars.length < rt.assign_cnt;

  return (
    <>
      <tr
        // `group` — 노선 칸의 `+ 수당·공제` 가 줄에 올렸을 때만 보인다.
        // 접힌 줄끼리는 가는 선, **펼친 노선만** 굵은 선으로 갈라 차량 묶음의 시작을 보인다
        // (전 줄 굵은 선 + 회색 바탕이면 접힌 목록이 줄무늬 벽이 된다).
        className={`group cursor-pointer hover:bg-brand-50 ${
          showCars ? 'border-t-2 border-line bg-brand-50/60' : ''
        }`}
        onClick={onToggle}
        title={showCars ? '눌러서 전체 노선으로 돌아가기' : '눌러서 이 노선만 보기(배정 차량 펴기)'}
      >
        {cols.map((c) => (
          <td key={c.key} className={`td ${c.align === 'right' ? 'num' : ''} ${clip(c)}`}>
            {c.route(rt, showCars, onBulk, onEditCost, onEditCommute)}
          </td>
        ))}
      </tr>

      {showCars &&
        cars.map((c, i) => (
          <tr key={i}>
            {cols.map((col) => (
              <td key={col.key} className={`td ${col.align === 'right' ? 'num' : ''} ${clip(col)}`}>
                {col.car(c, onAddReq)}
              </td>
            ))}
          </tr>
        ))}

      {truncated && (
        <tr>
          <td className="td pl-6 text-xs text-amber-700" colSpan={cols.length}>
            이 노선은 {rt.assign_cnt}대 중 {cars.length}대만 표시됩니다 — 목록 상한에 걸렸습니다.
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * 상세는 **배정 목록·노선 요약·총계가 한 응답**으로 온다. `useList` 는 `rows` 만 다루므로
 * 나머지를 같이 받는 얇은 래퍼를 둔다 — 요청을 나눠 보내면 셋의 필터가 갈릴 수 있다.
 */
function useDetail(recvId: number, driveMon: string, nonce: number) {
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [drivers, setDrivers] = useState<Record<string, unknown>[]>([]);
  const [directCars, setDirectCars] = useState<DirectCar[]>([]);
  const r = useList(async (alive) => {
    const res = await api.listResultCustDetail({ recvId, driveMon, limit: 200 });
    if (alive()) {
      setRoutes((res.routes ?? []) as unknown as RouteRow[]);
      setDrivers(res.drivers ?? []);
      setDirectCars(res.directCars ?? []);
    }
    return res as unknown as { rows: Row[] };
  }, [recvId, driveMon, nonce]);
  return { ...r, routes, drivers, directCars };
}

/**
 * 영업정산 **일괄 등록** — 배정마다 금액만 적고 한 번에 저장한다.
 *
 * 왜 축·계정을 위에서 한 번만 고르는가: 실측상 이 표는 한 축에 몰려 있고
 * (40,665건 중 97.6%가 기사 가감) 계정도 한 접수 안에서 같은 이름이 반복된다.
 * 배정마다 네 칸에 계정까지 고르게 만들면 칸이 수십 개가 되고 대부분 늘 비어 있다.
 * 축이 둘 이상 필요하면 저장하고 축을 바꿔 한 번 더 — 각각이 별개 항목이라 그게 정확하다.
 *
 * ⚠️ **덮어쓰기가 아니라 새 항목**이다. 한 배정에 계정이 다른 항목이 여럿 달릴 수 있어
 *    "그 배정의 금액"이라는 단일 대상이 없다. 이미 달린 건수를 줄마다 보여주고,
 *    고치거나 지우는 건 항목 단위 화면(/영업정산)이 한다.
 * ⚠️ 배정 스냅샷이 없는 과거 통근 행은 붙일 대상이 없어 **목록에 안 뜬다**(확정분이라 그게 맞다).
 */
function BulkReqPanel({
  driveMon,
  rows,
  routes,
  scope,
  onScope,
  isAdmin,
  onClose,
  onDone,
}: {
  driveMon: string;
  rows: Row[];
  routes: RouteRow[];
  /** 대상 범위 — 전체 / 이 노선 / 이 기사. */
  scope: BulkScope;
  onScope: (s: BulkScope) => void;
  isAdmin: boolean;
  /** 팝업을 닫는 유일한 길(여백·Esc 로는 안 닫힌다). */
  onClose: () => void;
  onDone: () => void;
}) {
  const [kind, setKind] = useState<ReqAccountKind>('기사지급');
  const [account, setAccount] = useState('');
  const [comment, setComment] = useState('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [fill, setFill] = useState('');
  /**
   * 고른 줄 — 한 노선에 차가 여럿일 때 **원하는 차에만** 넣는 길(#116 배상준).
   * 고른 줄이 있으면 「같은 금액 채우기」가 그 줄에만 들어간다. 저장 대상은 여전히 「금액을 적은 줄」이다.
   */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 붙일 수 있는 줄만 — 배정 id 가 있어야 서버가 6개 ID 를 편다 — 을 **범위로 한 번 더** 거른다.
   * ⚠️ 범위를 바꾸면 이미 적은 금액은 **그대로 둔다.** 화면에서 사라진 줄은 저장 대상에서도
   *    빠지므로(아래 `entered` 가 targets 기준이다) 잘못 들어갈 일이 없고,
   *    노선을 오갈 때 적어 둔 값을 잃지 않는다.
   */
  const targets = useMemo(
    () =>
      rows.filter(
        (r) =>
          reqTargetOf(r) &&
          (scope.kind === 'all' ||
            (scope.kind === 'route' && Number(r.dispt_id) === scope.id) ||
            (scope.kind === 'driver' && Number(r.driver_id) === scope.id)),
      ),
    [rows, scope],
  );
  const routeName = useMemo(
    () => new Map(routes.map((rt) => [Number(rt.dispt_id), rt.dispt_name ?? ''])),
    [routes],
  );

  const pickedShown = targets.filter((r) => picked.has(reqKeyOf(r)));
  const togglePick = (key: string) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allPicked = targets.length > 0 && pickedShown.length === targets.length;

  const entered = targets.filter((r) => Number(amounts[reqKeyOf(r)] || 0) !== 0);
  const sum = entered.reduce((a, r) => a + Number(amounts[reqKeyOf(r)] || 0), 0);
  const isMinus = kind === '기사공제' || kind === '거래처공제';

  /**
   * 저장 전에 **한 번 되짚는다**. 오기입력의 실제 모양은 둘이다:
   *   ① 자릿수 — 10,000 을 100,000 으로. 줄마다 보면 안 보이고 **합계로 보면 보인다.**
   *   ② 중복 — 이미 수당이 달린 배정에 또 넣기(이 화면은 덮어쓰지 않고 **더한다**).
   * 그래서 확인 단계에 합계·줄수·계정과 **이미 등록된 줄 수**를 같이 띄운다.
   */
  const [confirming, setConfirming] = useState(false);
  const dupes = entered.filter((r) => Number(r.req_cnt ?? 0) > 0);
  // 확인 화면을 띄운 뒤 금액·계정·구분을 고치면 **확인은 무효**다 — 안 그러면 확인한 것과 다른 게 들어간다.
  useEffect(() => { setConfirming(false); }, [amounts, kind, account, scope]);
  const maxAmt = entered.reduce((a, r) => Math.max(a, Math.abs(Number(amounts[reqKeyOf(r)] || 0))), 0);

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const r = await api.saveResultReqBulk({
        driveMon,
        kind,
        accountName: account,
        comment: comment.trim() || undefined,
        items: entered.map((t) => ({
          ...reqTargetOf(t)!,
          amount: amounts[reqKeyOf(t)] ?? '',
        })),
      });
      // 저장하면 상세를 다시 읽는다 — 차익·수령액이 그 자리에서 움직이는 게 이 화면의 요점이다.
      alert(`${r.created}건 등록했습니다 (${r.kind} · ${r.accountName}).`);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      // 실패했으면 확인 단계를 풀어 고칠 수 있게 한다.
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3 p-4">
      {/* 지금 무엇을 대상으로 넣고 있는지 — 범위가 안 보이면 노선 하나에 넣을 것을 전체에 넣는다. */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">수당·공제 등록</span>
        <span className="rounded bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
          {scope.kind === 'all'
            ? '전체 배정'
            : `${scope.kind === 'route' ? '노선' : '기사'} · ${scope.label}`}
        </span>
        <span className="text-xs text-ink-faint">{targets.length}대</span>
        {scope.kind !== 'all' && (
          <button className="btn-ghost px-1.5 py-0 text-xs" onClick={() => onScope({ kind: 'all' })}>
            전체로
          </button>
        )}
        <span className="ml-auto text-xs text-ink-faint">{driveMon} 정산분</span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-ink-soft">
          <span className="mb-1 block">항목 구분</span>
          <select
            className="field w-36"
            value={kind}
            onChange={(e) => { setKind(e.target.value as ReqAccountKind); setAccount(''); }}
          >
            <option value="기사지급">기사 지급(+)</option>
            <option value="기사공제">기사 공제(−)</option>
            <option value="거래처청구">거래처 청구(+)</option>
            <option value="거래처공제">거래처 공제(−)</option>
            <option value="수익비용">수익 비용(지급 아님)</option>
          </select>
        </label>
        <label className="text-xs text-ink-soft">
          <span className="mb-1 block">계정</span>
          <AccountField kind={kind} value={account} onChange={setAccount} />
        </label>
        <label className="text-xs text-ink-soft">
          <span className="mb-1 block">비고 (전 줄 공통)</span>
          <input className="field w-56" value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
        <label className="text-xs text-ink-soft">
          <span className="mb-1 block">같은 금액 채우기</span>
          <span className="flex items-center gap-1">
            <MoneyInput className="w-28" value={fill} onChange={setFill} />
            <button
              className="btn-ghost text-xs"
              type="button"
              onClick={() =>
                pickedShown.length > 0
                  ? setAmounts((cur) => ({
                      ...cur,
                      ...Object.fromEntries(pickedShown.map((t) => [reqKeyOf(t), fill])),
                    }))
                  : setAmounts(Object.fromEntries(targets.map((t) => [reqKeyOf(t), fill])))
              }
            >
              {pickedShown.length > 0 ? `고른 ${pickedShown.length}줄에` : '전 줄에'}
            </button>
          </span>
        </label>
        <button className="btn-ghost text-xs" type="button" onClick={() => setAmounts({})}>
          비우기
        </button>
      </div>

      {kind === '기사공제' && account === '선지급' && !isAdmin && (
        <p className="text-xs text-amber-700">‘선지급’ 공제는 등록한 뒤에는 관리자만 고치거나 지울 수 있습니다.</p>
      )}
      {error && <ErrorBox message={error} />}

      {targets.length === 0 ? (
        <p className="text-xs text-ink-soft">
          붙일 수 있는 배정이 없습니다 — 아직 차량이 배정되지 않았거나, 통근이면 「통근 운행일수」에서 그 달 기사 줄을 먼저 만들어야 합니다.
        </p>
      ) : (
        <div className="max-h-80 overflow-y-auto rounded-lg border border-line bg-surface">
          <table className="w-full">
            <thead className="sticky top-0 bg-canvas">
              <tr>
                <th className="th w-8">
                  <input
                    type="checkbox"
                    aria-label="전 줄 고르기"
                    checked={allPicked}
                    onChange={() =>
                      setPicked(allPicked ? new Set() : new Set(targets.map((t) => reqKeyOf(t))))
                    }
                  />
                </th>
                <th className="th">노선</th>
                <th className="th">차량 · 기사</th>
                <th className="th text-right">운행료</th>
                <th className="th text-right">이미 등록</th>
                <th className="th text-right">금액</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((t) => {
                const key = reqKeyOf(t);
                // 금액이 들어간 줄은 칠해 둔다 — 같은 노선의 여러 차 중 **어느 차에 들어가는지** 한눈에.
                const has = Number(amounts[key] || 0) !== 0;
                return (
                  <tr key={key} className={has ? 'bg-brand-50' : picked.has(key) ? 'bg-canvas' : undefined}>
                    <td className="td">
                      <input
                        type="checkbox"
                        aria-label="이 줄 고르기"
                        checked={picked.has(key)}
                        onChange={() => togglePick(key)}
                      />
                    </td>
                    <td className="td text-sm">{dash(routeName.get(Number(t.dispt_id)) || t.dispt_name)}</td>
                    <td className="td text-sm">
                      {dash(t.car_no)} · {dash(t.driver_name)}
                      {t.driver_kind ? (
                        <span className="ml-2 rounded bg-canvas px-1.5 py-0.5 text-xs text-ink-soft">{kindLabel(t.driver_kind)}</span>
                      ) : null}
                      <span className="ml-2 text-xs text-ink-faint">{dash(t.owner_name)}</span>
                    </td>
                    <td className="td num text-sm text-ink-soft">{money(t.cost_driver_total)}</td>
                    <td className="td num text-xs">
                      {Number(t.req_cnt ?? 0) > 0 ? (
                        <ReqAmounts plus={t.driver_plus} minus={t.driver_minus} count={Number(t.req_cnt)} />
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </td>
                    <td className="td num">
                      <MoneyInput
                        className="w-28"
                        value={amounts[key] ?? ''}
                        onChange={(v) => setAmounts((cur) => ({ ...cur, [key]: v }))}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 확인 단계 — 줄마다 보면 안 보이는 자릿수 실수가 **합계와 최대금액**에서 드러난다. */}
      {confirming && (
        <div className="space-y-1 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm">
          <p className="font-semibold text-amber-900">아래 내용으로 등록합니다. 맞습니까?</p>
          <p className="tabular-nums text-amber-900">
            <b>{kind}</b> · 계정 <b>{account}</b> · <b>{entered.length}줄</b> · 합계{' '}
            <b>
              {isMinus ? '−' : '+'}
              {money(sum)}
            </b>{' '}
            <span className="text-xs">(한 줄 최대 {money(maxAmt)})</span>
          </p>
          {/* 어느 차에 들어가는지 이름으로 되짚는다 — 같은 노선에 차가 여럿이면 줄 위치만으론 헷갈린다(#116). */}
          {entered.length <= 10 && (
            <p className="break-keep text-xs text-amber-900">
              {entered.map((t) => `${dash(t.car_no)}(${dash(t.driver_name)})`).join(' · ')}
            </p>
          )}
          {dupes.length > 0 && (
            <p className="text-xs text-amber-900">
              ⚠️ 이 중 <b>{dupes.length}줄</b>에는 이미 등록된 항목이 있습니다 — 이 등록은 덮어쓰지 않고{' '}
              <b>더해집니다</b>.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-ink-soft">
          입력 <b>{entered.length}</b>줄 · 합계{' '}
          <b className={isMinus ? 'text-red-700' : 'text-emerald-700'}>
            {isMinus ? '−' : '+'}
            {money(sum)}
          </b>
        </span>
        {/* 팝업은 여백·Esc 로 안 닫힌다 — 닫는 길은 이 두 버튼뿐이다. */}
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={busy}>
          취소
        </button>
        {confirming ? (
          <>
            <button className="btn-ghost" onClick={() => setConfirming(false)} disabled={busy}>
              다시 보기
            </button>
            <button className="btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? '등록 중…' : `네, ${entered.length}건 등록합니다`}
            </button>
          </>
        ) : (
          <button
            className="btn-primary"
            disabled={busy || entered.length === 0 || !account}
            onClick={() => setConfirming(true)}
          >
            {entered.length}건 등록
          </button>
        )}
      </div>

      <p className="break-keep text-xs text-ink-soft">
        금액을 적은 줄만 등록됩니다. <b>기존 항목을 덮어쓰지 않고 새 항목을 만듭니다</b> —
        같은 배정에 계정이 다른 항목이 여러 개 붙을 수 있기 때문입니다.
        고치거나 지우려면 <b>영업정산</b> 화면에서 항목을 여세요.
        {rows.length >= 200 && (
          <b className="ml-1 text-amber-700">
            ⚠ 배정 목록이 200줄에서 잘렸습니다 — 여기 보이는 줄만 등록됩니다.
          </b>
        )}
      </p>
    </div>
  );
}

/**
 * 노선 한 줄의 **금액 두 칸**만 고치는 폼 (2026-09-17 사용자 요청:
 * "마감 전까지는 청구금액과 운행료를 자유롭게 수정할 수 있어야 편할 것 같아").
 *
 * 🔴 **운행료는 1대당 단가를 받는다.** 화면이 보여주는 노선 운행료는 "단가 x 대수" 라
 *    총액을 받아 대수로 나누면 나눠떨어지지 않는 날 금액이 어긋난다(CLAUDE.md 불변식).
 *    그래서 입력칸은 단가이고, 그 옆에 "x N대 = 총액"을 **계산해 보여준다** — 사람이 암산하지 않게.
 * 🔴 **마감 판정은 서버가 한다**(`assertMonthOpen`). 여기서 한 번 더 판정하면 두 규칙이 갈린다 —
 *    막히면 서버 문구를 그대로 띄운다.
 * ⚠️ 금액은 **문자열 그대로** 보낸다. Number 로 왕복시키면 소수가 깎인다.
 */
function RouteCostForm({
  route,
  driveMon,
  onCancel,
  onSaved,
}: {
  route: RouteRow;
  driveMon: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  // 칸에는 쉼표를 찍어 보여주고(#23), 보낼 때·계산할 때는 벗긴다.
  // 포함가로 청구하는 노선은 포함가를 칸에 띄운다(#87) — 공급가를 띄우면 다시 저장할 때 ÷1.1 이 또 걸린다.
  // 「청구액(포함)」 칸에서 열면(#247) 포함 모드로 — 저장된 포함가, 없으면 공급가 × 1.1 반올림을 띄운다.
  const hasAddTax = Number(route.cost_cust_add_tax_unit ?? 0) > 0;
  const inclAtOpen = isVatIncl(route.cost_cust_unit, route.cost_cust_add_tax_unit) || (route.edit_incl === true && hasAddTax);
  const [vatIncl, setVatIncl] = useState(inclAtOpen || route.edit_incl === true);
  const [cust, setCust] = useState(moneyInput(inclAtOpen ? route.cost_cust_add_tax_unit
    : route.edit_incl && route.cost_cust_unit != null ? String(Math.round(Number(route.cost_cust_unit) * 1.1)) : route.cost_cust_unit));
  const [driver, setDriver] = useState(moneyInput(route.cost_driver_unit));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cnt = Number(route.assign_cnt || 0);
  const driverTotal = driver === '' ? null : Number(uncomma(driver)) * cnt;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.setDisptCost({
        disptId: Number(route.dispt_id),
        costCust: cust === '' ? null : uncomma(cust),
        costDriver: driver === '' ? null : uncomma(driver),
        vatIncl,
      });
      onSaved();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4 p-4">
      <div>
        <h2 className="text-lg font-bold">청구액·운행료 수정</h2>
        <p className="mt-1 text-sm text-ink-soft">
          {driveMon} · {dash(route.dispt_name)} · 배정 {cnt}대
          <br />
          <span className="text-ink-faint">마감하면 이 수정은 막힙니다.</span>
        </p>
      </div>

      {err && <ErrorBox message={err} />}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <span className="mb-1 block text-sm text-ink-soft">청구액 (노선 전체)</span>
          <div className="mb-1">
            <VatToggle value={vatIncl} onChange={setVatIncl} disabled={busy} />
          </div>
          <MoneyInput value={cust} onChange={setCust} placeholder="예: 945,236" />
          <p className="mt-1"><VatPreview text={cust} incl={vatIncl} /></p>
        </div>
        <div>
          <span className="mb-1 block text-sm text-ink-soft">운행료 — 차량 1대당 단가</span>
          <MoneyInput value={driver} onChange={setDriver} placeholder="예: 350,000" />
          {/* 단가 x 대수를 **화면이 대신 곱해 준다** — 이 곱을 사람이 하다가 틀리는 자리다. */}
          <p className="mt-1 text-xs text-ink-faint">
            {driverTotal === null || !Number.isFinite(driverTotal)
              ? '단가를 넣으면 총액을 보여줍니다.'
              : `x ${cnt}대 = ${money(String(driverTotal))} (노선 운행료)`}
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onCancel} disabled={busy}>
          취소
        </button>
        <button className="btn-primary" onClick={save} disabled={busy}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>
    </div>
  );
}
